import { useCallback, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useInfiniteQuery, useQueries, useQuery } from '@zg-rentals/react-query5';
import {
  RentalManagerAccountViewZrmV2WebApiHandler_inquiries,
  RentalManagerAccountViewZrmV2WebApiHandler_leases,
  RentalManagerAccountViewZrmV2WebApiHandler_list,
  RentalManagerAccountViewZrmV2WebApiHandler_payments,
  RentalManagerAccountViewZrmV2WebApiHandler_units,
} from '@zg-rentals/ts-rental-manager-api-client';

import type { UseQueryOptions } from '@zg-rentals/react-query5';
import type {
  AccountViewListingGroupDto,
  AccountViewListingGroupsResponseDto,
  AccountViewMultiplexUnitsResponseDto,
  AccountViewTableCellDto,
  ListingAlias,
  ListingCountDto,
  RentalManagerKeyPagerDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';

export type ListParams = {
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includePropertyOnly?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
  startKeyExclusive?: string;
};

type UnitsParams = {
  groupId: string;
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
  startKeyExclusive?: string;
};

// Generic params type for cell-based endpoints (payments, inquiries, leases)
type CellDataParams = {
  groupId: string;
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includeUnarchived?: boolean;
  listingAlias?: ListingAlias;
  searchText?: string;
};

// Type aliases for backward compatibility
type PaymentsParams = CellDataParams;
type InquiriesParams = CellDataParams;
type LeasesParams = CellDataParams;

// Generic helper to construct API params by removing undefined/falsy optional fields
export function cleanApiParams<T extends Record<string, unknown>>(params: T, fieldsToCheck: Array<keyof T>): T {
  const apiParams = { ...params };

  for (const field of fieldsToCheck) {
    const value = apiParams[field];
    if (value === undefined || (typeof value === 'string' && !value)) {
      delete apiParams[field];
    }
  }

  return apiParams;
}

function getCellDataApiParams<T extends CellDataParams>(params: T): T {
  return cleanApiParams(params, [
    'listingAlias',
    'searchText',
    'includeActive',
    'includeInactive',
    'includeArchived',
    'includeUnarchived',
  ]);
}

type CellDataQueryOptions = Omit<UseQueryOptions<AccountViewTableCellDto, Error>, 'queryKey' | 'queryFn'>;

// Generic hook factory for cell-based data endpoints
function useGetCellData(
  params: CellDataParams,
  queryKeyPrefix: string,
  hookName: string,
  apiFn: (params: CellDataParams) => Promise<AccountViewTableCellDto>,
  options?: CellDataQueryOptions,
) {
  const {
    groupId,
    listingAlias,
    includeActive,
    includeInactive,
    includeArchived,
    includeUnarchived,
    searchText = '',
  } = params;

  const { data, isPending, isError, isFetching, refetch } = useQuery<AccountViewTableCellDto, Error>({
    queryKey: [
      queryKeyPrefix,
      groupId,
      listingAlias,
      searchText,
      includeActive,
      includeInactive,
      includeArchived,
      includeUnarchived,
    ],
    queryFn: async () => {
      if (!params.groupId?.trim()) {
        logError({
          error: new Error(`${hookName}: groupId is required`),
          context: hookName,
        });

        throw new Error('groupId is required');
      }

      const apiParams = getCellDataApiParams(params);

      return apiFn(apiParams);
    },
    // Keep previous data during refetch to prevent UI flicker
    placeholderData: (previousData) => previousData,
    ...options,
  });

  return {
    data,
    isPending,
    isFetching,
    isError,
    refetch,
  };
}

// Exported hooks using the generic factory
type PaymentsQueryOptions = CellDataQueryOptions;

export const useGetPropertiesPayment = (params: PaymentsParams, options?: PaymentsQueryOptions) =>
  useGetCellData(
    params,
    'getPropertiesPayment',
    'useGetPropertiesPayment',
    RentalManagerAccountViewZrmV2WebApiHandler_payments,
    options,
  );

type InquiriesQueryOptions = CellDataQueryOptions;

export const useGetPropertiesInquiries = (params: InquiriesParams, options?: InquiriesQueryOptions) =>
  useGetCellData(
    params,
    'getPropertiesInquiries',
    'useGetPropertiesInquiries',
    RentalManagerAccountViewZrmV2WebApiHandler_inquiries,
    options,
  );

type LeasesQueryOptions = CellDataQueryOptions;

export const useGetPropertyLeases = (params: LeasesParams, options?: LeasesQueryOptions) =>
  useGetCellData(
    params,
    'getLeases',
    'useGetPropertyLeases',
    RentalManagerAccountViewZrmV2WebApiHandler_leases,
    options,
  );

// Params accepted by the hook: ListParams or the return type of transformFiltersToApiParams (extra keys are ignored by the API).
export type GroupablePropertiesListParams = ListParams | ReturnType<typeof transformFiltersToApiParams>;

function getListApiParams(params: GroupablePropertiesListParams): Omit<ListParams, 'startKeyExclusive'> {
  return cleanApiParams(
    {
      includeActive: params.includeActive,
      includeArchived: params.includeArchived,
      includeInactive: params.includeInactive,
      includeUnarchived: params.includeUnarchived,
      limit: params.limit,
      searchText: params.searchText,
    },
    ['searchText', 'includeActive', 'includeInactive', 'includeArchived', 'includeUnarchived', 'limit'],
  );
}

// Flattened shape returned when pages are loaded; empty object when no data yet
export type GroupablePropertiesData = {
  listingGroups?: Array<AccountViewListingGroupDto>;
  listingCount?: ListingCountDto;
  pager?: RentalManagerKeyPagerDto;
};

export type UseGetPropertiesListOptions = {
  enabled?: boolean;
};

export const useGetPropertiesList = (
  params: GroupablePropertiesListParams,
  options: UseGetPropertiesListOptions = {},
) => {
  const { enabled = true } = options;
  const { includeActive, includeArchived, includeInactive, includeUnarchived, limit, searchText = '' } = params;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } =
    useInfiniteQuery<
      AccountViewListingGroupsResponseDto,
      Error,
      { pages: Array<AccountViewListingGroupsResponseDto> },
      Array<string | boolean | number | undefined>,
      string
    >({
      queryKey: [
        'getGroupableProperties',
        searchText,
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        limit,
      ],
      queryFn: async ({ pageParam }) => {
        const apiParams = getListApiParams(params);

        return RentalManagerAccountViewZrmV2WebApiHandler_list({
          ...apiParams,
          startKeyExclusive: pageParam,
        });
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      enabled,
      // Keep previous data during refetch to prevent UI flicker
      placeholderData: (previousData) => {
        if (!previousData?.pages?.length) {
          return;
        }

        return previousData;
      },
      meta: {
        tags: ['getProperties'],
      },
    });

  const propertiesData: GroupablePropertiesData = data?.pages.length
    ? {
        listingGroups: data.pages.flatMap((page) => page.listingGroups),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    error,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export type MultiplexUnitsListParams = Omit<UnitsParams, 'groupId' | 'startKeyExclusive'>;

function getUnitsApiParams(params: MultiplexUnitsListParams): Omit<UnitsParams, 'groupId' | 'startKeyExclusive'> {
  return cleanApiParams(
    {
      includeActive: params.includeActive,
      includeArchived: params.includeArchived,
      includeInactive: params.includeInactive,
      includeUnarchived: params.includeUnarchived,
      limit: params.limit,
      searchText: params.searchText,
    },
    ['searchText', 'includeActive', 'includeInactive', 'includeArchived', 'includeUnarchived', 'limit'],
  );
}

export type UseGetMultiplexUnitsDataOptions = {
  enabled?: boolean;
};

/**
 * Fetches units for multiple group IDs in parallel with infinite-query style pagination per group.
 *
 * Pagination flow:
 * - We keep per group an array of startKeyExclusive values: [''], then ['', endKey1], etc.
 * - Each entry becomes one query (first page uses '', next pages use the previous response's pager.endKey).
 * - Calling fetchNextPageForGroup(groupId, pager.endKey) appends that key; on re-render we build one more
 *   query for that group, and useQueries runs it, so the next page is fetched.
 */
export const useGetMultiplexUnits = (
  params: MultiplexUnitsListParams,
  groupIds: Array<string>,
  options: UseGetMultiplexUnitsDataOptions = {},
) => {
  const { enabled = true } = options;
  const { includeActive, includeArchived, includeInactive, includeUnarchived, limit, searchText = '' } = params;

  // Per group: array of startKeyExclusive values. First is '' (first page); each additional is the previous page's pager.endKey.
  const [startKeysByGroupId, setStartKeysByGroupId] = useState<Record<string, Array<string>>>({});

  // One query per (groupId, startKeyExclusive). Adding a startKey causes one more query and thus one more fetch.
  const queries = groupIds.flatMap((groupId) => {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    return startKeys.map((startKeyExclusive) => ({
      queryKey: [
        'getMultiplexUnits',
        searchText,
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        limit,
        groupId,
        startKeyExclusive,
      ] as const,
      queryFn: async () => {
        if (!groupId?.trim()) {
          logError({
            error: new Error('useGetMultiplexUnits: groupId is required'),
            context: 'useGetMultiplexUnits',
          });

          throw new Error('groupId is required');
        }

        const apiParams: UnitsParams = {
          ...getUnitsApiParams(params),
          groupId,
          limit,
          startKeyExclusive: startKeyExclusive || undefined,
        };

        return RentalManagerAccountViewZrmV2WebApiHandler_units(apiParams);
      },
      enabled: enabled && !!groupId,
      meta: {
        tags: ['getMultiplexUnits', groupId],
        context: {
          api: 'RentalManagerAccountViewZrmV2WebApiHandler_units',
          details: 'useGetMultiplexUnits',
        },
      },
    }));
  });

  const results = useQueries({ queries });

  const dataByGroupId: Record<string, Partial<AccountViewMultiplexUnitsResponseDto>> = {};
  const hasNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingByGroupId: Record<string, boolean> = {};

  // Merge all pages per group and derive hasNextPage / isFetchingNextPage / isFetchingByGroupId from results.
  let resultIndex = 0;
  for (const groupId of groupIds) {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    const groupResults = results.slice(resultIndex, resultIndex + startKeys.length);
    const pages = groupResults.map((r) => r.data).filter((d): d is AccountViewMultiplexUnitsResponseDto => d != null);
    if (pages.length > 0) {
      dataByGroupId[groupId] = {
        units: pages.flatMap((p) => p.units),
        listingCount: pages[0].listingCount,
        pager: pages[pages.length - 1].pager,
      };
      hasNextPageByGroupId[groupId] = pages[pages.length - 1].pager?.hasMore ?? false;
    }
    for (let p = 0; p < groupResults.length; p++) {
      if (groupResults[p]?.isFetching) {
        isFetchingByGroupId[groupId] = true;
        if (p >= 1) {
          isFetchingNextPageByGroupId[groupId] = true;
        }
        break;
      }
    }
    resultIndex += startKeys.length;
  }

  /**
   * Load the next page for a group. Appending nextStartKeyExclusive to state causes one more query
   * to be built on the next render, so useQueries runs it and the next page is fetched.
   * Pass the current last page's pager.endKey as nextStartKeyExclusive.
   */
  const fetchNextPageForGroup = useCallback((groupId: string, nextStartKeyExclusive: string) => {
    if (!nextStartKeyExclusive) return;
    setStartKeysByGroupId((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] ?? ['']), nextStartKeyExclusive],
    }));
  }, []);

  return {
    dataByGroupId,
    results,
    isPending: results.some((r) => r.isPending),
    isFetching: results.some((r) => r.isFetching),
    isFetchingByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingNextPageByGroupId,
  };
};
